<template>
  <multiselect
    v-model="selectedValue"
    :options="stopOptions"
    :multiple="false"
    :searchable="true"
    :show-labels="false"
    :allowEmpty="false"
    :maxHeight="240"
    label="name"
    track-by="key"
    class="min-w-250p w-auto"
    :placeholder="$t('flexigo.ridership.selectStop')"
    @input="handleChange"
  >
    <span slot="noOptions">
      {{ $t('flexigo.ridership.noData') }}
    </span>
  </multiselect>
</template>

<script>
import Multiselect from 'vue-multiselect';
import { mapGetters } from 'vuex';

const ALL_STOPS_KEY = '__all_stops__';

export default {
  name: 'RidershipStopFilter',
  components: { Multiselect },
  props: {
    selectedStopIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedValue: null,
    };
  },
  computed: {
    ...mapGetters('ridershipDashboard', { stops: 'availableStops' }),
    /**
     * One row per stop name — matches the sibling route filter's shape and the
     * spec's "same stop names must be grouped" rule. The list endpoint already
     * dedupes by name, so this mainly guards against a duplicate slipping
     * through; `ids[0]` stays the id we filter on, so the single-stop contract
     * of /ridership/stops is unchanged.
     */
    uniqueStops() {
      const seen = new Map();
      this.stops.forEach(({ name, id }) => {
        if (!seen.has(name)) seen.set(name, { name, ids: [id] });
        else seen.get(name).ids.push(id);
      });
      return Array.from(seen.values());
    },
    /**
     * Prepend an explicit "All Stops" entry so users can deselect by picking it
     * from the dropdown instead of relying on the non-intuitive X chip button.
     * `allowEmpty` is now false — the All Stops option replaces the empty state.
     */
    stopOptions() {
      return [
        { key: ALL_STOPS_KEY, name: this.$t('flexigo.ridership.allStops'), ids: [] },
        ...this.uniqueStops.map((s) => ({ ...s, key: s.name })),
      ];
    },
  },
  watch: {
    /**
     * Mirror the incoming selection. The parent re-resolves the selected stop
     * BY NAME whenever the list is refetched — the list endpoint returns a
     * different id for the same stop depending on the route filter
     * (VSER-12588 QA finding 3) — so this must re-sync on every prop change,
     * not only on user input. Binding through v-model also removes the need
     * for the DOM value re-assert the previous native <select> required.
     */
    selectedStopIds: {
      immediate: true,
      handler(newIds) {
        if (!newIds || newIds.length === 0) {
          [this.selectedValue] = this.stopOptions;
          return;
        }
        const idSet = new Set(newIds);
        this.selectedValue =
          this.stopOptions.find((opt) => opt.key !== ALL_STOPS_KEY && opt.ids.some((id) => idSet.has(id))) ||
          this.stopOptions[0];
      },
    },
  },
  methods: {
    // "All Stops" (ids: []) emits an empty array — the parent treats that as
    // "no stop filter".
    handleChange(value) {
      if (!value || value.key === ALL_STOPS_KEY || !value.ids?.length) {
        this.$emit('change', []);
        return;
      }
      this.$emit('change', [value.ids[0]]);
    },
  },
};
</script>
